import React from 'react';
import { SecureSEO } from '@/utils/secureSEO';

const BestColdStorageWallets = () => {
  const publishDate = "2024-12-15T08:00:00+00:00";
  const modifiedDate = "2024-12-15T08:00:00+00:00";
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Best Cold Storage Crypto Wallets Guide 2025",
    "description": "Discover the best cold storage wallets for securing your cryptocurrency in 2025. Learn about hardware wallets like SafePal and their security features.",
    "image": "https://mdbapp.io/authors/dtdevmdbappio.jpg",
    "author": {
      "@type": "Person",
      "name": "Daniel T.",
      "url": "https://mdbapp.io/authors/dtdevmdbappio"
    },
    "publisher": {
      "@type": "Organization",
      "name": "MDB App",
      "logo": {
        "@type": "ImageObject",
        "url": "https://mdbapp.io/logo.png"
      }
    },
    "datePublished": publishDate,
    "dateModified": modifiedDate,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://mdbapp.io/blog/best-cold-storage-wallets"
    }
  };

  const metaTags = [
    { type: 'name', name: 'description', content: 'Discover the best cold storage wallets for securing your cryptocurrency in 2025. Learn about hardware wallets like SafePal and their security features.' },
    { type: 'name', name: 'author', content: 'Daniel T.' },
    { type: 'name', name: 'robots', content: 'index, follow' },
    { type: 'name', name: 'keywords', content: 'cold storage wallet, crypto hardware wallet, SafePal, Ellipal, cryptocurrency security, air-gapped wallet, crypto storage, hardware wallet comparison, best crypto wallets 2025' },
    
    { type: 'property', name: 'og:title', content: 'Best Cold Storage Crypto Wallets Guide 2025' },
    { type: 'property', name: 'og:description', content: 'Discover the best cold storage wallets for securing your cryptocurrency in 2025. Learn about hardware wallets like SafePal and their security features.' },
    { type: 'property', name: 'og:type', content: 'article' },
    { type: 'property', name: 'og:url', content: 'https://mdbapp.io/blog/best-cold-storage-wallets' },
    { type: 'property', name: 'og:image', content: 'https://mdbapp.io/authors/dtdevmdbappio.jpg' },
    { type: 'property', name: 'article:published_time', content: publishDate },
    { type: 'property', name: 'article:modified_time', content: modifiedDate },
    { type: 'property', name: 'article:author', content: 'https://mdbapp.io/authors/dtdevmdbappio' },
    { type: 'property', name: 'article:section', content: 'Cryptocurrency' },
    { type: 'property', name: 'article:tag', content: 'Cold Storage' },
    { type: 'property', name: 'article:tag', content: 'Hardware Wallet' },
    
    { type: 'name', name: 'twitter:card', content: 'summary_large_image' },
    { type: 'name', name: 'twitter:title', content: 'Best Cold Storage Crypto Wallets Guide 2025' },
    { type: 'name', name: 'twitter:description', content: 'Discover the best cold storage wallets for securing your cryptocurrency in 2025. Learn about hardware wallets like SafePal and their security features.' },
    { type: 'name', name: 'twitter:image', content: 'https://mdbapp.io/authors/dtdevmdbappio.jpg' },
    { type: 'name', name: 'twitter:creator', content: '@mdbappio' }
  ];

  const linkTags = [
    { rel: 'canonical', href: 'https://mdbapp.io/blog/best-cold-storage-wallets' }
  ];

  return (
    <>
      <SecureSEO 
        title="Best Cold Storage Crypto Wallets Guide 2025 | MDB App"
        metaTags={metaTags}
        structuredData={articleStructuredData}
        linkTags={linkTags}
      />

      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <header className="mb-12 text-center">
          <div className="mb-10">
            <h1 className="mb-8 text-4xl font-bold text-[#0a74ff] leading-[1.4]">Best Cold Storage Crypto Wallets</h1>
            
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-full overflow-hidden bg-gradient-to-r from-[#0a74ff] to-[#FFD700] p-[2px]">
                <div className="w-full h-full rounded-full overflow-hidden">
                  <img 
                    src="/authors/dtdevmdbappio.jpg"
                    alt="Daniel T."
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src = 'https://api.dicebear.com/7.x/initials/svg?seed=DT&backgroundColor=0a74ff';
                    }}
                  />
                </div>
              </div>
              <div>
                <div className="font-medium text-gray-200 leading-relaxed">Daniel T. (MDB Dev)</div>
                <div className="text-gray-400 leading-relaxed">Published on December 15, 2024</div>
              </div>
            </div>

            <h1 className="text-4xl font-bold text-white mb-8 leading-[1.4]">Complete Guide 2025</h1>

            <div className="mt-8 text-gray-400 leading-relaxed">
              Published on December 15, 2024 • Last Updated December 15, 2024 • 2 min read
            </div>
          </div>

          <div className="mt-12 mb-16 max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8 leading-[1.4]">What is the Air-gapped Cold Wallet?</h2>
            <p className="text-lg text-gray-300 leading-[1.8]">
              Air-gapped cold wallet is always 100% isolated from the internet. It is fully protected from unauthorized access, cyber hacks, malware, and any online threats.
            </p>
          </div>
        </header>

        <article className="prose prose-invert max-w-none">
          <section>
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="flex items-center gap-1">
                  <img 
                    src="/SFPLOGODARK.png" 
                    alt="SafePal Logo" 
                    className="w-auto h-9"
                  />
                  <span className="text-3xl font-bold text-white md:text-4xl">SafePal</span>
                </div>
              </div>
              <p className="mb-4 text-lg text-gray-300 leading-[1.8]">
                Experience seamless token creation with SafePal Wallet integration.
                Our platform is fully optimized for SafePal users, providing a smooth
                and secure token deployment process.
              </p>
              <div className="flex justify-center mb-8">
                <img 
                  src="/Safepal MdbApp.png" 
                  alt="SafePal MdbApp Partnership" 
                  className="w-full h-auto max-w-md"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="p-6 rounded-lg border-2 border-[#0a74ff] bg-transparent">
                  <div className="flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h3 className="mb-2 text-xl font-bold text-white leading-[1.4]">Easy Connection</h3>
                  <p className="text-gray-300 leading-[1.8]">Connect your SafePal wallet with just one click</p>
                </div>
                <div className="p-6 rounded-lg border-2 border-[#0a74ff] bg-transparent">
                  <div className="flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h3 className="mb-2 text-xl font-bold text-white leading-[1.4]">Secure Crypto Management</h3>
                  <p className="text-gray-300 leading-[1.8]">Manage your tokens with confidence using SafePal's secure platform</p>
                </div>
                <div className="p-6 rounded-lg border-2 border-[#0a74ff] bg-transparent">
                  <div className="flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h3 className="mb-2 text-xl font-bold text-white leading-[1.4]">Optimized Integration</h3>
                  <p className="text-gray-300 leading-[1.8]">Fully optimized for SafePal users with seamless integration</p>
                </div>
              </div>
              <div className="flex justify-center mb-8">
                <img 
                  src="/s1_01 (2).webp" 
                  alt="SafePal S1 Hardware Wallet" 
                  className="w-full max-w-xl h-auto"
                />
              </div>
              <div className="flex justify-center mb-8">
                <img 
                  src="/S1_CypherV2.png" 
                  alt="SafePal S1 Cypher V2 Hardware Wallet" 
                  className="w-full max-w-[60%] h-auto rounded-lg shadow-lg"
                />
              </div>
              <div className="flex justify-center gap-4">
                <a 
                  href="https://www.safepal.com/store/s1?ref=mdbappio" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-8 py-4 text-sm font-semibold text-[#0a74ff] bg-transparent border-2 border-[#0a74ff] rounded-lg hover:bg-[#0a74ff] hover:text-white transition-all duration-200"
                >
                  Get your SafePal Wallet
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 ml-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </a>
                <a 
                  href="https://safepal.com/download" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-8 py-4 text-sm font-semibold text-[#0a74ff] bg-transparent border-2 border-[#0a74ff] rounded-lg hover:bg-[#0a74ff] hover:text-white transition-all duration-200"
                >
                  Download SafePal App
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 ml-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </a>
              </div>
            </div>
          </section>

          <div className="my-16">
            <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#EAB308] to-transparent"></div>
          </div>

          <section>
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="flex items-center gap-1">
                  <img 
                    src="/ELLIPALLOGODARK.png" 
                    alt="Ellipal Logo" 
                    className="w-auto h-9"
                  />
                  <span className="text-3xl font-bold text-white md:text-4xl">llipal</span>
                </div>
              </div>
              <p className="mb-4 text-lg text-gray-300 leading-[1.8]">
                Experience seamless token creation with Ellipal Wallet integration.
                Our platform is fully optimized for Ellipal users, providing a smooth
                and secure token deployment process.
              </p>
              <p className="mb-8 text-xl font-semibold text-[#0a74ff] leading-[1.4]">
                ELLIPAL Supports 10,000+ coins & tokens
              </p>
              <div className="flex justify-center mb-8">
                <img 
                  src="/250116-home-banner-01.webp" 
                  alt="Ellipal Supported Cryptocurrencies" 
                  className="w-full max-w-2xl h-auto rounded-lg"
                />
              </div>
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-white mb-4 leading-[1.4]">Air-Gapped & Decentralized</h3>
                <p className="text-lg font-semibold text-[#0a74ff] mb-4 leading-[1.8]">Simple, Secure, 100% offline.</p>
                <p className="text-lg text-gray-300 mb-4 leading-[1.8]">
                  No WiFi, Bluetooth, USB, or networks—just scan a QR code for private transactions.
                </p>
                <p className="text-lg text-gray-300 mb-8 leading-[1.8]">
                  Full control is in your hands: sign what you see, and trust what you sign.
                </p>
                <div className="flex justify-center mb-8">
                  <img 
                    src="/Air-gapped-qrr-code.webp" 
                    alt="Air-gapped QR Code Transaction" 
                    className="w-full max-w-lg h-auto rounded-lg"
                  />
                </div>
              </div>
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-white mb-4 leading-[1.4]">4.0-inch Display Makes Verify Easier</h3>
                <p className="text-lg text-gray-300 mb-4 leading-[1.8]">
                  Sign with Confidence: No more blind signing. Our 4.0-inch display shows exactly what you're signing, trust what you see.
                </p>
                <p className="text-lg text-gray-300 mb-8 leading-[1.8]">
                  Full Touchscreen: Buttons are ancient history. Swipe into the future with ease.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="p-6 rounded-lg border-2 border-[#0a74ff] bg-transparent">
                  <h4 className="text-xl font-semibold text-white mb-2 leading-[1.4]">PIN & Passphrase Protection</h4>
                  <p className="text-gray-300 leading-[1.8]">
                    Pattern Pin & Password entry. Get extra secure with gesture pin and numeric passwords.
                  </p>
                </div>

                <div className="p-6 rounded-lg border-2 border-[#0a74ff] bg-transparent">
                  <h4 className="text-xl font-semibold text-white mb-2 leading-[1.4]">Secret Secondary Wallet</h4>
                  <p className="text-gray-300 leading-[1.8]">
                    Supports a backup account with a separate password, ensuring your primary assets stay secure even in extreme scenarios.
                  </p>
                </div>

                <div className="p-6 rounded-lg border-2 border-[#0a74ff] bg-transparent">
                  <h4 className="text-xl font-semibold text-white mb-2 leading-[1.4]">Full Metal-Sealed Body</h4>
                  <p className="text-gray-300 leading-[1.8]">
                    A bulwark against physical, supply chain attacks, and evil maid attacks, ensuring your assets remain secure.
                  </p>
                </div>

                <div className="p-6 rounded-lg border-2 border-[#0a74ff] bg-transparent">
                  <h4 className="text-xl font-semibold text-white mb-2 leading-[1.4]">PCI Level Self-Destruction</h4>
                  <p className="text-gray-300 leading-[1.8]">
                    If any tampering is detected, your private information will be automatically deleted.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="p-6 rounded-lg border-2 border-[#0a74ff] bg-transparent">
                  <div className="flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h3 className="mb-2 text-xl font-bold text-white leading-[1.4]">Easy Connection</h3>
                  <p className="text-gray-300 leading-[1.8]">Connect your Ellipal wallet with just one click</p>
                </div>
                <div className="p-6 rounded-lg border-2 border-[#0a74ff] bg-transparent">
                  <div className="flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h3 className="mb-2 text-xl font-bold text-white leading-[1.4]">Secure Crypto Management</h3>
                  <p className="text-gray-300 leading-[1.8]">Manage your tokens with confidence using Ellipal's secure platform</p>
                </div>
                <div className="p-6 rounded-lg border-2 border-[#0a74ff] bg-transparent">
                  <div className="flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h3 className="mb-2 text-xl font-bold text-white leading-[1.4]">Optimized Integration</h3>
                  <p className="text-gray-300 leading-[1.8]">Fully optimized for Ellipal users with seamless integration</p>
                </div>
              </div>
              <div className="flex justify-center gap-4">
                <a 
                  href="https://www.ellipal.com/?rfsn=8558077.9cc2ae" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-8 py-4 text-sm font-semibold text-[#0a74ff] bg-transparent border-2 border-[#0a74ff] rounded-lg hover:bg-[#0a74ff] hover:text-white transition-all duration-200"
                >
                  Get your Ellipal Cold Wallet
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 ml-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </a>
                <a
                  href="https://www.ellipal.com/pages/ellipal-app" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-8 py-4 text-sm font-semibold text-[#0a74ff] bg-transparent border-2 border-[#0a74ff] rounded-lg hover:bg-[#0a74ff] hover:text-white transition-all duration-200"
                >
                  Download Ellipal App
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 ml-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </a>
              </div>
            </div>
          </section>
        </article>
      </div>
    </>
  );
};

export default BestColdStorageWallets;
